<template>
  <div class="list-repair-slips main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Phiếu sửa chữa'">
        <template v-slot:toolbar>
          <b-button
            v-if="canExport"
            variant="success"
            size="sm"
            class="mr-2"
            :disabled="exporting"
            @click="onExportExcel"
          >
            <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
            <i v-else class="far fa-file-excel"></i>
            {{ exporting ? 'Đang xuất...' : 'Xuất Excel' }}
          </b-button>
          <b-button
            v-if="canCreate"
            variant="primary"
            size="sm"
            @click="goToCreate"
          >
            <i class="fas fa-plus"></i> Tạo phiếu mới
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filter bar — pattern theo trang Đơn hàng -->
          <b-row class="mb-4">
            <b-col>
              <div class="date-style">
                <b-input-group>
                  <date-picker
                    placeholder="Từ"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-input-group>
                <span class="ml-1 mr-1"></span>
                <b-input-group>
                  <date-picker
                    placeholder="Đến"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-input-group>
              </div>
            </b-col>
            <b-col>
              <b-form-select
                size="sm"
                v-model="filter.status"
                :options="statusFilterOptions"
              ></b-form-select>
            </b-col>
            <b-col>
              <b-input
                placeholder="Nhập số phiếu, KH, SĐT, IMEI, sản phẩm"
                size="sm"
                v-model.trim="filter.search"
                autocomplete="off"
                v-on:keyup.enter="onFilter"
              ></b-input>
            </b-col>
            <b-col>
              <b-button-group size="sm">
                <b-button
                  class="font-weight-bolder btn-filter-apply"
                  variant="primary"
                  @click="onFilter"
                >
                  <i class="fas fa-search text-white fa-lg"></i>Lọc
                </b-button>
                <b-button
                  variant="warning"
                  @click="onResetFilter"
                  v-b-tooltip.hover
                  title="Xoá bộ lọc"
                >
                  <i class="fas fa-redo text-white"></i>
                </b-button>
              </b-button-group>
            </b-col>
          </b-row>

          <!-- Table -->
          <b-table
            :items="rows"
            :fields="fields"
            :busy="loading"
            bordered
            hover
            show-empty
            empty-text="Không có phiếu sửa chữa nào"
            class="table-vertical-center"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Đang tải...</strong>
              </div>
            </template>

            <template #cell(stt)="data">
              {{ (page - 1) * limit + data.index + 1 }}
            </template>

            <template #cell(slipNumber)="{ item }">
              <div>
                <strong>{{ item.slipNumber }}</strong>
              </div>
              <div class="text-muted small">
                {{ formatDateTime(item.createdAt) }}
              </div>
            </template>

            <template #cell(customer)="{ item }">
              <div>{{ item.customerName }}</div>
              <div class="text-muted small">{{ item.customerPhone }}</div>
            </template>

            <template #cell(product)="{ item }">
              <div>{{ item.productName || '—' }}</div>
              <div class="text-muted small">
                IMEI: {{ item.imei || '—' }}
                <b-badge v-if="item.isExternalDevice" variant="warning" class="ml-1">
                  Hàng ngoài
                </b-badge>
              </div>
            </template>

            <template #cell(warrantyProductName)="{ item }">
              <span v-if="item.warrantyProductName">{{ item.warrantyProductName }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(status)="{ item }">
              <b-badge :variant="statusVariant(item.status)">
                {{ statusLabel(item.status) }}
              </b-badge>
            </template>

            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" no-caret right variant="link" toggle-class="p-0">
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="goToEdit(row.item.id)">
                    <span class="action-item-default">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Xem / Sửa
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="canDelete && row.item.status === SLIP_STATUS_RECEIVING"
                    @click="onDelete(row.item)"
                  >
                    <span class="action-item-danger">
                      <i class="flaticon2-rubbish-bin icon-size-sm"></i>
                      &nbsp; Xoá
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <b-row class="mt-3">
            <b-col>
              <p class="text-dark mb-0 list-total-label">
                Tổng số phiếu: {{ totalRow }}
              </p>
            </b-col>
            <b-col>
              <b-pagination
                v-show="totalPage >= 2"
                v-model="page"
                :total-rows="totalRow"
                :per-page="limit"
                align="right"
                size="sm"
                @change="onPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { checkPermission } from '@/utils/saveDataToLocal';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { DP_CONFIG } from '@/utils/date';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import fileDownload from '@/utils/file-download';
import { SLIP_STATUS, SLIP_STATUS_OPTIONS as STATUS_OPTIONS } from '@/utils/warranty-service.constants';
import { extractError, formatDateTime, toIsoDate } from '@/utils/warranty-service.helpers';

const STATUS_FILTER_OPTIONS = Object.freeze([
  { value: '', text: 'Tất cả trạng thái' },
  ...STATUS_OPTIONS.map((s) => ({ value: s.value, text: s.text })),
]);

const TABLE_FIELDS = Object.freeze([
  { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '4%' } },
  { key: 'slipNumber', label: 'Số phiếu', thStyle: { width: '13%' } },
  { key: 'customer', label: 'Khách hàng', thStyle: { width: '17%' } },
  { key: 'product', label: 'Thiết bị', thStyle: { width: '20%' } },
  { key: 'warrantyProductName', label: 'Loại BH (SP bảo hành)', thStyle: { width: '17%' } },
  { key: 'status', label: 'Trạng thái', tdClass: 'text-center', thStyle: { width: '13%' } },
  { key: 'actions', label: 'Thao tác', tdClass: 'text-center', thStyle: { width: '8%' } },
]);

const initialFilter = () => ({
  search: '',
  status: '',
  fromDate: '',
  toDate: '',
});

const API_BASE = 'v2/warranty-service/repair-slips';

export default {
  name: 'ListRepairSlips',
  components: { KTCodePreview },
  data() {
    return {
      rows: [],
      loading: false,
      filter: initialFilter(),
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      exporting: false,
      dpConfigs: DP_CONFIG,
    };
  },
  computed: {
    // Expose frozen constants — không reactive-wrap mỗi instance.
    statusFilterOptions: () => STATUS_FILTER_OPTIONS,
    fields: () => TABLE_FIELDS,
    // Expose status value cho template (chỉ delete được khi phiếu còn ở receiving).
    SLIP_STATUS_RECEIVING: () => SLIP_STATUS.RECEIVING,
    canCreate() {
      return checkPermission('WARRANTY_SERVICE_CREATE');
    },
    canDelete() {
      return checkPermission('WARRANTY_SERVICE_DELETE');
    },
    canExport() {
      return checkPermission('WARRANTY_SERVICE_EXPORT');
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Phiếu sửa chữa' },
    ]);
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await ApiService.query(API_BASE, {
          params: {
            search: this.filter.search,
            status: this.filter.status,
            fromDate: toIsoDate(this.filter.fromDate),
            toDate: toIsoDate(this.filter.toDate),
            page: this.page,
            limit: this.limit,
          },
        });
        const payload = data.data || {};
        this.rows = payload.dataset || [];
        this.totalRow = payload.totalRow || 0;
        this.totalPage = payload.totalPage || 0;
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.fetchData();
    },
    onResetFilter() {
      this.filter = initialFilter();
      this.page = 1;
      this.fetchData();
    },
    async onExportExcel() {
      this.exporting = true;
      try {
        const response = await ApiService.query('warranty-service-export/repair-slips', {
          params: {
            search: this.filter.search,
            status: this.filter.status,
            fromDate: toIsoDate(this.filter.fromDate),
            toDate: toIsoDate(this.filter.toDate),
          },
          responseType: 'blob',
        });
        let filename = `phieu-sua-chua-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
        try {
          filename = fileDownload.getFileName(response);
        } catch (_e) {
          // BE không trả Content-Disposition đúng format → fallback tên trên
        }
        fileDownload.createFile(
          response.data,
          filename,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
        makeToastSuccess('Đã xuất file Excel');
      } catch (err) {
        // Lỗi BE trả blob (vì responseType=blob) — phải đọc text trước
        let msg = 'Không xuất được file';
        if (err && err.response && err.response.data) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            msg = json.message || msg;
          } catch (_parseErr) {
            // ignore
          }
        }
        makeToastFaile(msg);
      } finally {
        this.exporting = false;
      }
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },

    statusLabel(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.text : value;
    },
    statusVariant(value) {
      const opt = STATUS_OPTIONS.find((o) => o.value === value);
      return opt ? opt.variant : 'secondary';
    },
    formatDateTime(value) {
      return formatDateTime(value);
    },

    goToCreate() {
      this.$router.push({ name: 'create-repair-slip' });
    },
    goToEdit(id) {
      this.$router.push({ name: 'edit-repair-slip', params: { id } });
    },
    async onDelete(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá phiếu "${item.slipNumber}" của ${item.customerName}?`,
        {
          title: 'Xác nhận xoá',
          okTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;
      try {
        await ApiService.delete(`${API_BASE}/${item.id}`);
        makeToastSuccess('Đã xoá phiếu sửa chữa');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list-repair-slips {
  ::v-deep .table-vertical-center td {
    vertical-align: middle;
  }
  // Đồng bộ pattern date-range "Từ" + "Đến" của trang Đơn hàng
  ::v-deep .date-style {
    display: flex;
    align-items: center;
    .input-group {
      flex: 1;
    }
  }

  .btn-filter-apply {
    width: 70px;
  }

  .list-total-label {
    font-weight: 500;
  }

  ::v-deep .action-item-default {
    color: #3f4254;
    font-size: 12px;
  }

  ::v-deep .action-item-danger {
    color: #f64e60;
    font-size: 12px;
  }
}
</style>
